import LOCATIONS from '../constants/locations';

const NOMINATIM_BASE = 'https://nominatim.openstreetmap.org';
const MIN_INTERVAL_MS = 1000;

let lastCallAt = 0;

// Nominatim's usage policy caps unauthenticated traffic at ~1 req/sec.
// All search/reverse calls across the app share this single gate.
async function throttledFetch(url, options) {
  const now = Date.now();
  const wait = Math.max(0, lastCallAt + MIN_INTERVAL_MS - now);
  if (wait > 0) await new Promise(resolve => setTimeout(resolve, wait));
  lastCallAt = Date.now();
  return fetch(url, options);
}

// Approximate centers used as a fallback when Nominatim's text fields don't
// line up with one of our emirate names (e.g. localized/alternate naming).
const EMIRATE_CENTERS = {
  'Abu Dhabi': [24.4539, 54.3773],
  'Ajman': [25.4052, 55.5136],
  'Al Ain': [24.2075, 55.7447],
  'Al Dhafra': [23.6, 53.7],
  'Dubai': [25.2048, 55.2708],
  'Fujairah': [25.1288, 56.3265],
  'Ras Al Khaimah': [25.7895, 55.9432],
  'Sharjah': [25.3463, 55.4209],
  'Um Al Quwain': [25.5647, 55.5534],
};

// Best-effort match of a Nominatim address component to one of our emirates.
// Returns '' rather than guessing wrong if nothing matches.
function matchLocation(address = {}) {
  const candidates = [address.state, address.city, address.county].filter(Boolean);
  for (const candidate of candidates) {
    const match = LOCATIONS.find(loc => loc.toLowerCase() === candidate.toLowerCase());
    if (match) return match;
  }
  return '';
}

// The emirate must always be one of our known options — falls back to
// whichever emirate's center is geographically closest to the pin.
function nearestLocation(latitude, longitude) {
  let closest = '';
  let minDist = Infinity;
  for (const loc of LOCATIONS) {
    const [lat, lon] = EMIRATE_CENTERS[loc];
    const dist = (lat - latitude) ** 2 + (lon - longitude) ** 2;
    if (dist < minDist) {
      minDist = dist;
      closest = loc;
    }
  }
  return closest;
}

function parseAddressLine(address = {}) {
  const road = address.road || address.street || '';
  const suburb = address.suburb || address.neighbourhood || address.quarter || '';
  return [road, suburb].filter(Boolean).join(', ');
}

export async function reverseGeocode(latitude, longitude, { signal } = {}) {
  const url = `${NOMINATIM_BASE}/reverse?lat=${latitude}&lon=${longitude}&format=json`;
  const res = await throttledFetch(url, { signal });
  const data = await res.json();
  const address = data.address || {};

  return {
    displayName: data.display_name || '',
    address_line: parseAddressLine(address),
    building: address.building || '',
    location: matchLocation(address) || nearestLocation(latitude, longitude),
  };
}

export async function searchAddress(query, { signal } = {}) {
  if (!query || query.trim().length < 3) return [];

  const params = new URLSearchParams({
    q: query,
    format: 'json',
    countrycodes: 'ae',
    limit: '5',
  });
  const res = await throttledFetch(`${NOMINATIM_BASE}/search?${params}`, { signal });
  const data = await res.json();

  return data.map(item => ({
    displayName: item.display_name,
    latitude: parseFloat(item.lat),
    longitude: parseFloat(item.lon),
  }));
}
